import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Person, ProfileMembership } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'
import type { MembershipLevel, MembershipLevelsResponse } from '@/pages/MembershipLevelsPage.types'

interface MembershipResponse {
  membership: ProfileMembership
}

const EMPTY_MEMBERSHIP_LEVELS: MembershipLevel[] = []

export function ChangeMembershipLevelDialog({
  person,
  membership,
  onClose,
  onChanged,
}: {
  person: Person
  membership: ProfileMembership
  onClose: () => void
  onChanged: () => Promise<void>
}) {
  const levelsQuery = useQuery({
    queryKey: ['membership-levels', 'available-for-assignment'],
    queryFn: () =>
      getJSON<MembershipLevelsResponse>('/api/v1/membership_levels?available_for_assignment=true'),
    retry: false,
  })
  const levels = (levelsQuery.data?.membership_levels ?? EMPTY_MEMBERSHIP_LEVELS).filter(
    (level) => level.name !== membership.level,
  )
  const [levelId, setLevelId] = React.useState('')
  const selectedLevel = levels.find((level) => String(level.id) === levelId)
  const noAlternateLevels = !levelsQuery.isLoading && !levelsQuery.isError && levels.length === 0
  const changeMembershipLevel = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/change_level`, {
        membership: {
          membership_level_id: levelId ? Number(levelId) : null,
        },
      }),
    onSuccess: onChanged,
  })
  const fieldErrors = membershipFieldErrors(changeMembershipLevel.error)
  const error = fieldErrors.status || fieldErrors.membershipLevel || fieldErrors.membership

  React.useEffect(() => {
    if (!levelId && levels[0]) setLevelId(String(levels[0].id))
  }, [levelId, levels])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            changeMembershipLevel.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Change level</DialogTitle>
            <DialogDescription>
              {person.name} · {membership.level}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {levelsQuery.isError && <Alert>Membership Levels unavailable.</Alert>}
            {error && <Alert>{error}</Alert>}
            {noAlternateLevels && <Alert>No other active Membership Levels are available.</Alert>}
            <MembershipLevelSelect
              levels={levels}
              value={levelId}
              label="New level"
              htmlFor="change-membership-level"
              error={fieldErrors.membershipLevel}
              loading={levelsQuery.isLoading}
              onChange={setLevelId}
            />
            {selectedLevel && (
              <PreviewRow
                label="Renewal due"
                value={levelChangePreview(membership, selectedLevel)}
              />
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={changeMembershipLevel.isPending || !selectedLevel}>
              {changeMembershipLevel.isPending ? 'Changing...' : 'Change level'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MembershipLevelSelect({
  levels,
  value,
  label,
  htmlFor,
  loading,
  error,
  onChange,
}: {
  levels: MembershipLevel[]
  value: string
  label: string
  htmlFor: string
  loading: boolean
  error?: string
  onChange: (value: string) => void
}) {
  return (
    <FormField label={label} htmlFor={htmlFor} error={error}>
      <Select value={value} disabled={loading || levels.length === 0} onValueChange={onChange}>
        <SelectTrigger id={htmlFor}>
          <SelectValue placeholder={loading ? 'Loading levels' : 'Select a level'} />
        </SelectTrigger>
        <SelectContent>
          {levels.map((level) => (
            <SelectItem key={level.id} value={String(level.id)}>
              {level.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

function membershipFieldErrors(error: Error | null) {
  return {
    membershipLevel: apiFieldMessages(error, 'membership_level')[0],
    membership: apiFieldMessages(error, 'membership')[0],
    status: apiFieldMessages(error, 'status')[0],
  }
}

function levelChangePreview(membership: ProfileMembership, selectedLevel: MembershipLevel) {
  return `${displayDate(membership.renewal_due_on)} -> ${displayDate(
    selectedLevel.level_change_renewal_due_on,
  )}`
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}
